import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle,
    XCircle,
    DollarSign,
    FileText,
    Clock,
    Building,
    Calendar,
    Wallet,
    CreditCard,
    Files,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { formatCurrency } from '@/lib/currency';
import { PaymentRequestStatus } from '@/types/database/payment-requests';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import {
    useBulkPaymentRequest,
    useBulkApprovePaymentRequests,
    useBulkRejectPaymentRequests,
    useBulkConfirmPaymentRequests,
} from '@/hooks/finance/usePaymentRequestsBulk';

type BulkPaymentFormItem = {
    paidFromAccountId: string;
    paymentDate: string;
    paymentReference: string;
};

const statusConfig: Record<
    PaymentRequestStatus,
    {
        label: string;
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
        icon: React.ReactNode;
    }
> = {
    draft: {
        label: 'Draft',
        variant: 'secondary',
        icon: <FileText className="h-4 w-4" />,
    },
    pending_approval: {
        label: 'Pending Approval',
        variant: 'outline',
        icon: <Clock className="h-4 w-4" />,
    },
    approved: {
        label: 'Approved',
        variant: 'default',
        icon: <CheckCircle className="h-4 w-4" />,
    },
    rejected: {
        label: 'Rejected',
        variant: 'destructive',
        icon: <XCircle className="h-4 w-4" />,
    },
    paid: {
        label: 'Paid',
        variant: 'default',
        icon: <DollarSign className="h-4 w-4" />,
    },
    cancelled: {
        label: 'Cancelled',
        variant: 'secondary',
        icon: <XCircle className="h-4 w-4" />,
    },
};

const parsePaymentReference = (ref: string | null) => {
    if (!ref) {
        return { bankAccount: null, reference: null };
    }

    const parts = ref.split(' | ');

    if (parts.length >= 2) {
        return {
            bankAccount: parts[0],
            reference: parts.slice(1).join(' | '),
        };
    }

    return {
        bankAccount: null,
        reference: ref,
    };
};

const getTodayDateString = () => new Date().toISOString().split('T')[0];

const PaymentRequestDetailBulkPage = () => {
    const navigate = useNavigate();
    const { id = '' } = useParams<{ id: string }>();
    const { user } = useAuth();

    const { data: group, isLoading } = useBulkPaymentRequest(id);
    const bulkApprove = useBulkApprovePaymentRequests();
    const bulkReject = useBulkRejectPaymentRequests();
    const bulkConfirmPayment = useBulkConfirmPaymentRequests();
    const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);

    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [comments, setComments] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [userProfile, setUserProfile] = useState<{ role: string } | null>(null);
    const [paymentDetailsByRequestId, setPaymentDetailsByRequestId] = useState<Record<string, BulkPaymentFormItem>>({});

    useEffect(() => {
        if (!group?.requests?.length) return;

        const today = getTodayDateString();

        setPaymentDetailsByRequestId((prev) => {
            const next: Record<string, BulkPaymentFormItem> = {};

            group.requests.forEach((request) => {
                next[request.id] = prev[request.id] ?? {
                    paidFromAccountId: '',
                    paymentDate: today,
                    paymentReference: '',
                };
            });

            return next;
        });
    }, [group?.uploadId, group?.requests]);

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!user?.id) return;

            const { data } = await supabase
                .from('user_profiles')
                .select('role')
                .eq('id', user.id)
                .maybeSingle();

            setUserProfile(data);
        };

        fetchUserProfile();
    }, [user?.id]);

    const updatePaymentDetails = (
        requestId: string,
        field: keyof BulkPaymentFormItem,
        value: string
    ) => {
        setPaymentDetailsByRequestId((prev) => ({
            ...prev,
            [requestId]: {
                ...(prev[requestId] ?? {
                    paidFromAccountId: '',
                    paymentDate: getTodayDateString(),
                    paymentReference: '',
                }),
                [field]: value,
            },
        }));
    };

    const resetPaymentDetails = () => {
        if (!group?.requests?.length) {
            setPaymentDetailsByRequestId({});
            return;
        }

        const today = getTodayDateString();

        setPaymentDetailsByRequestId(
            Object.fromEntries(
                group.requests.map((request) => [
                    request.id,
                    {
                        paidFromAccountId: '',
                        paymentDate: today,
                        paymentReference: '',
                    },
                ])
            )
        );
    };

    const handleApproveAll = async () => {
        if (!group) return;

        await bulkApprove.mutateAsync({
            requests: group.requests,
            comments,
        });

        setApproveDialogOpen(false);
        setComments('');
    };

    const handleRejectAll = async () => {
        if (!group || !rejectionReason) return;

        await bulkReject.mutateAsync({
            requests: group.requests,
            reason: rejectionReason,
            comments,
        });

        setRejectDialogOpen(false);
        setRejectionReason('');
        setComments('');
    };

    const handleConfirmAllPayments = async () => {
        if (!group) return;

        const approvedRequests = group.requests.filter((request) => request.status === 'approved');

        if (approvedRequests.length === 0) {
            toast.error('There are no approved requests to confirm.');
            return;
        }

        const firstIncompleteRequest = approvedRequests.find(
            (request) => !paymentDetailsByRequestId[request.id]?.paidFromAccountId
        );

        if (firstIncompleteRequest) {
            toast.error(`Select "Paid From Account" for ${firstIncompleteRequest.request_number}.`);
            return;
        }

        const payments = approvedRequests.map((request) => {
            const paymentDetails = paymentDetailsByRequestId[request.id];
            const selectedAccount = bankAccounts?.find(
                (account) => account.id === paymentDetails.paidFromAccountId
            );

            // Store the bank reference token directly (no bank-name prefix).
            // Bank account is already linked via bankAccountId — prefix would pollute reconciliation matching.
            const combinedReference = paymentDetails.paymentReference || '';

            const totalAmount = request.total_amount || 0;

            return {
                id: request.id,
                paymentReference: combinedReference,
                actualPaymentDate: paymentDetails.paymentDate || getTodayDateString(),
                bankAccountId: paymentDetails.paidFromAccountId,
                baseAmount: totalAmount,
                vatRate: 0,
                vatAmount: 0,
                totalExpense: totalAmount,
                withholdingRate: 0,
                withholdingAmount: 0,
                netPayment: totalAmount,
            };
        });

        await bulkConfirmPayment.mutateAsync({ payments });

        setPaymentDialogOpen(false);
        resetPaymentDetails();
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-80 w-full" />
                </div>
            </Layout>
        );
    }

    if (!group) {
        return (
            <Layout>
                <div className="py-12 text-center">
                    <h2 className="text-xl font-semibold">Bulk request group not found</h2>
                    <Button className="mt-4" onClick={() => navigate(-1)}>
                        Go Back
                    </Button>
                </div>
            </Layout>
        );
    }

    const firstRequest = group.firstRequest;
    const bulkDescription = group.department?.name
        ? `Bulk payment requests for ${group.department.name} department`
        : 'Bulk payment requests';

    const uploadMethodLabels: Record<string, string> = {
        bulk_csv: 'CSV file',
    };

    const uploadMethodLabel =
        uploadMethodLabels[group.uploadMethod] ?? group.uploadMethod ?? 'Not specified';

    const canShowBulkActions =
        group.requests.length > 0 &&
        group.requests.every((request) => request.status === 'pending_approval');

    const approvedCount = group.requests.filter((r) => r.status === 'approved').length;
    const canConfirmBulkPayments = approvedCount > 0;

    const isFinanceUser =
        userProfile?.role === 'finance' || userProfile?.role === 'admin';

    const canRejectBulkAsFinance = isFinanceUser && approvedCount > 0;

    return (
        <Layout>
            <div className="mx-auto max-w-6xl space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>

                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-2xl font-bold text-foreground">Bulk Payment Request</h1>

                                <Badge variant="secondary" className="gap-1">
                                    <Files className="h-3 w-3" />
                                    <span>Bulk request</span>
                                </Badge>

                                {group.bulkStatus !== 'mixed' ? (
                                    <Badge variant={statusConfig[group.bulkStatus].variant}>
                                        {statusConfig[group.bulkStatus].icon}
                                        <span className="ml-1">{statusConfig[group.bulkStatus].label}</span>
                                    </Badge>
                                ) : (
                                    <Badge variant="outline">
                                        <Clock className="h-4 w-4" />
                                        <span className="ml-1">Mixed statuses</span>
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {canShowBulkActions && (
                            <>
                                <Button
                                    variant="destructive"
                                    onClick={() => setRejectDialogOpen(true)}
                                    disabled={bulkReject.isPending}
                                >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Reject All
                                </Button>

                                <Button
                                    onClick={() => setApproveDialogOpen(true)}
                                    disabled={bulkApprove.isPending}
                                >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Approve All
                                </Button>
                            </>
                        )}

                        {canConfirmBulkPayments && (
                            <>
                                {canRejectBulkAsFinance && (
                                    <Button
                                        variant="destructive"
                                        onClick={() => setRejectDialogOpen(true)}
                                        disabled={bulkReject.isPending}
                                    >
                                        <XCircle className="mr-2 h-4 w-4" />
                                        Reject{approvedCount < group.requests.length ? ` (${approvedCount})` : ' All'}
                                    </Button>
                                )}

                                <Button
                                    onClick={() => setPaymentDialogOpen(true)}
                                    disabled={bulkConfirmPayment.isPending}
                                >
                                    <DollarSign className="mr-2 h-4 w-4" />
                                    Confirm Payment{approvedCount < group.requests.length ? ` (${approvedCount} remaining)` : ''}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid gap-6 md:grid-cols-3">
                    <div className="space-y-6 md:col-span-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>Bulk request details</CardTitle>
                            </CardHeader>

                            <CardContent className="space-y-4">
                                {firstRequest.description && (
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                                        <p className="mt-1">{bulkDescription}</p>
                                    </div>
                                )}

                                <Separator />

                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Requests Count</p>
                                        <p className="text-xl font-semibold">{group.requestsCount}</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Amount</p>
                                        <p className="text-xl font-semibold">
                                            {formatCurrency(group.totalAmount, group.currency || 'ETB')}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-muted-foreground">Created</p>
                                        <p className="font-medium">{format(new Date(group.createdAt), 'MMM d, yyyy')}</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-muted-foreground">Upload Method</p>
                                        <p className="font-medium">{uploadMethodLabel}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Requests in this group</CardTitle>
                            </CardHeader>

                            <CardContent className="space-y-4">
                                {group.requests.map((request) => {
                                    const expense = request.expense?.[0];
                                    const { bankAccount, reference } = parsePaymentReference(
                                        request.payment_reference
                                    );

                                    return (
                                        <div key={request.id} className="space-y-4 rounded-lg border p-4">
                                            <div className="flex flex-wrap items-start justify-between gap-4">
                                                <div>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-mono text-sm text-muted-foreground">
                                                            {request.request_number}
                                                        </span>

                                                        <Badge variant={statusConfig[request.status].variant}>
                                                            {statusConfig[request.status].icon}
                                                            <span className="ml-1">{statusConfig[request.status].label}</span>
                                                        </Badge>
                                                    </div>

                                                    <h3 className="mt-1 font-medium">{request.title}</h3>

                                                    <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                                        {request.vendor_name && <span>{request.vendor_name}</span>}
                                                        {request.category?.name && <span>• {request.category.name}</span>}
                                                        <span>• {format(new Date(request.created_at), 'MMM d, yyyy')}</span>
                                                    </div>
                                                </div>

                                                <div className="text-right">
                                                    <p className="text-lg font-semibold">
                                                        {formatCurrency(request.total_amount || 0, request.currency || 'ETB')}
                                                    </p>

                                                    {request.preferred_payment_date && (
                                                        <p className="text-sm text-muted-foreground">
                                                            Due: {format(new Date(request.preferred_payment_date), 'MMM d, yyyy')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {request.items && request.items.length > 0 && (
                                                <div>
                                                    <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                                                        Line Items
                                                    </h4>

                                                    <div className="overflow-hidden rounded-lg border">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-muted">
                                                                <tr>
                                                                    <th className="p-3 text-left">Description</th>
                                                                    <th className="p-3 text-right">Qty</th>
                                                                    <th className="p-3 text-right">Unit Price</th>
                                                                    <th className="p-3 text-right">Amount</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {request.items.map((item) => (
                                                                    <tr key={item.id} className="border-t">
                                                                        <td className="p-3">{item.description}</td>
                                                                        <td className="p-3 text-right">{item.quantity}</td>
                                                                        <td className="p-3 text-right">
                                                                            {formatCurrency(item.unit_price, request.currency || 'ETB')}
                                                                        </td>
                                                                        <td className="p-3 text-right">
                                                                            {formatCurrency(item.amount, request.currency || 'ETB')}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>

                                                            <tfoot className="bg-muted/50">
                                                                <tr className="border-t">
                                                                    <td colSpan={3} className="p-3 text-right font-medium">
                                                                        Subtotal
                                                                    </td>
                                                                    <td className="p-3 text-right">
                                                                        {formatCurrency(request.amount || 0, request.currency || 'ETB')}
                                                                    </td>
                                                                </tr>

                                                                {!!request.vat_applicable && (
                                                                    <tr>
                                                                        <td colSpan={3} className="p-3 text-right font-medium">
                                                                            VAT
                                                                        </td>
                                                                        <td className="p-3 text-right">
                                                                            {formatCurrency(
                                                                                request.vat_amount || 0,
                                                                                request.currency || 'ETB'
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                <tr className="font-semibold">
                                                                    <td colSpan={3} className="p-3 text-right">
                                                                        Total
                                                                    </td>
                                                                    <td className="p-3 text-right">
                                                                        {formatCurrency(
                                                                            request.total_amount || 0,
                                                                            request.currency || 'ETB'
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {(request.status === 'paid' || expense) && (
                                                <div className="grid gap-6 sm:grid-cols-2">
                                                    {expense && (
                                                        <div className="space-y-2 rounded-lg bg-muted/50 p-4 text-sm">
                                                            <h4 className="mb-3 font-medium">Financial Summary</h4>

                                                            <div className="flex justify-between">
                                                                <span className="text-muted-foreground">Base Amount</span>
                                                                <span>{formatCurrency(expense.amount, request.currency || 'ETB')}</span>
                                                            </div>

                                                            {expense.vat_rate && expense.vat_rate > 0 && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-muted-foreground">
                                                                        VAT ({expense.vat_rate}%)
                                                                    </span>
                                                                    <span>
                                                                        +{' '}
                                                                        {formatCurrency(
                                                                            expense.vat_amount || 0,
                                                                            request.currency || 'ETB'
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div className="flex justify-between border-t border-border pt-2 font-medium">
                                                                <span>Total Expense</span>
                                                                <span>
                                                                    {formatCurrency(expense.total_amount, request.currency || 'ETB')}
                                                                </span>
                                                            </div>

                                                            {expense.withholding_rate && expense.withholding_rate > 0 && (
                                                                <>
                                                                    <div className="flex justify-between text-muted-foreground">
                                                                        <span>Withholding ({expense.withholding_rate}%)</span>
                                                                        <span>
                                                                            -{' '}
                                                                            {formatCurrency(
                                                                                expense.withholding_amount || 0,
                                                                                request.currency || 'ETB'
                                                                            )}
                                                                        </span>
                                                                    </div>

                                                                    <div className="flex justify-between border-t border-border pt-2 font-semibold">
                                                                        <span>Net Payment</span>
                                                                        <span>
                                                                            {formatCurrency(
                                                                                expense.net_payment || expense.total_amount,
                                                                                request.currency || 'ETB'
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="space-y-4">
                                                        {request.actual_payment_date && (
                                                            <div className="flex items-center gap-3">
                                                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                                                <div>
                                                                    <p className="text-sm text-muted-foreground">Payment Date</p>
                                                                    <p className="font-medium">
                                                                        {format(new Date(request.actual_payment_date), 'MMM d, yyyy')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {bankAccount && (
                                                            <div className="flex items-center gap-3">
                                                                <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                <div>
                                                                    <p className="text-sm text-muted-foreground">Paid From</p>
                                                                    <p className="font-medium">{bankAccount}</p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {reference && (
                                                            <div className="flex items-center gap-3">
                                                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                                                <div>
                                                                    <p className="text-sm text-muted-foreground">Reference</p>
                                                                    <p className="font-medium">{reference}</p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {request.paid_at && (
                                                            <div className="border-t pt-3 text-xs text-muted-foreground">
                                                                Confirmed on{' '}
                                                                {format(new Date(request.paid_at), "MMM d, yyyy 'at' HH:mm")}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Summary</CardTitle>
                            </CardHeader>

                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Building className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">Department</p>
                                        <p className="font-medium">{group.department?.name || 'Not specified'}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">Categories</p>
                                        <p className="font-medium">
                                            {group.categories.length > 0
                                                ? group.categories.map((cat) => cat.name).join(', ')
                                                : 'Not specified'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">Preferred Payment Date</p>
                                        <p className="font-medium">
                                            {firstRequest.preferred_payment_date
                                                ? format(new Date(firstRequest.preferred_payment_date), 'MMM d, yyyy')
                                                : 'Not specified'}
                                        </p>
                                    </div>
                                </div>

                                <Separator />

                                <div>
                                    <p className="text-sm text-muted-foreground">Total Amount</p>
                                    <p className="text-2xl font-bold">
                                        {formatCurrency(group.totalAmount, group.currency || 'ETB')}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Approve Bulk Payment Request</DialogTitle>
                        <DialogDescription>
                            You are about to approve all payment requests in this group for{' '}
                            {formatCurrency(group.totalAmount, group.currency || 'ETB')}.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Comments (optional)</Label>
                            <Textarea
                                placeholder="Add any comments..."
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleApproveAll} disabled={bulkApprove.isPending}>
                            {bulkApprove.isPending ? 'Approving...' : 'Approve All'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reject Bulk Payment Request</DialogTitle>
                        <DialogDescription>
                            Please provide a reason for rejecting all payment requests in this group.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Rejection Reason *</Label>
                            <Textarea
                                placeholder="Enter the reason for rejection..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Comments (optional)</Label>
                            <Textarea
                                placeholder="Add any comments..."
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleRejectAll}
                            disabled={!rejectionReason || bulkReject.isPending}
                        >
                            {bulkReject.isPending ? 'Rejecting...' : 'Reject All'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Confirm Bulk Payment</DialogTitle>
                        <DialogDescription>
                            Review each payment in this group and provide payment details. VAT and withholding are not applied for bulk confirmation.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                        {group.requests.map((request) => {
                            const paymentDetails =
                                paymentDetailsByRequestId[request.id] ?? {
                                    paidFromAccountId: '',
                                    paymentDate: getTodayDateString(),
                                    paymentReference: '',
                                };

                            return (
                                <div key={request.id} className="space-y-4 rounded-lg border p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-mono text-sm text-muted-foreground">
                                                    {request.request_number}
                                                </span>
                                                <Badge variant={statusConfig[request.status].variant}>
                                                    {statusConfig[request.status].icon}
                                                    <span className="ml-1">{statusConfig[request.status].label}</span>
                                                </Badge>
                                            </div>

                                            <h3 className="mt-1 font-medium">{request.title}</h3>

                                            <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                                {request.vendor_name && <span>{request.vendor_name}</span>}
                                                {request.category?.name && <span>• {request.category.name}</span>}
                                            </div>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-sm text-muted-foreground">Amount</p>
                                            <p className="text-lg font-semibold">
                                                {formatCurrency(request.total_amount || 0, request.currency || group.currency || 'ETB')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-2">
                                            <Label>Paid From Account *</Label>
                                            <Select
                                                value={paymentDetails.paidFromAccountId}
                                                onValueChange={(value) =>
                                                    updatePaymentDetails(request.id, 'paidFromAccountId', value)
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue
                                                        placeholder={
                                                            bankAccountsLoading ? 'Loading...' : 'Select bank account'
                                                        }
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {bankAccounts?.map((account) => (
                                                        <SelectItem key={account.id} value={account.id}>
                                                            {account.name}{' '}
                                                            {account.bank_name ? `(${account.bank_name})` : ''}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Payment Date</Label>
                                            <Input
                                                type="date"
                                                value={paymentDetails.paymentDate}
                                                onChange={(e) =>
                                                    updatePaymentDetails(request.id, 'paymentDate', e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Payment Reference</Label>
                                            <Input
                                                placeholder="Transaction ID or reference number"
                                                value={paymentDetails.paymentReference}
                                                onChange={(e) =>
                                                    updatePaymentDetails(request.id, 'paymentReference', e.target.value)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmAllPayments}
                            disabled={bankAccountsLoading || bulkConfirmPayment.isPending}
                        >
                            {bulkConfirmPayment.isPending ? 'Confirming...' : 'Confirm Payment'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default PaymentRequestDetailBulkPage;
